import React, { useState, useEffect, useRef } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { endpoints } from '../api/endpoints';
import { completeLogin } from '../utils/authSession';
import { Button } from './ui/Button';

const OtpVerification = ({ email, onSuccess, onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');
    
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
      const nextEmptyIndex = newOtp.findIndex(val => !val);
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[5]?.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post(endpoints.auth.verifyOtp, {
        email,
        otp: otpCode,
      });

      completeLogin(data);

      onSuccess();
    } catch (err) {
      setError(extractErrorMessage(err, 'Invalid OTP. Please try again.'));
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    
    setLoading(true);
    setError('');
    
    try {
      await api.post(endpoints.auth.resendOtp, null, {
        params: { email },
      });
      setTimer(300);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to resend OTP'));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-large border border-slate-200/80 p-8 sm:p-9 ring-1 ring-slate-900/5">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 flex items-center justify-center shadow-lg shadow-primary-900/15 ring-4 ring-primary-100/80">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">Enter verification code</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              A 6-digit code was sent to the owner admin.
              <br />
              Please ask them for the code to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* OTP Inputs */}
            <div className="flex justify-center gap-2 sm:gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className={`
                    w-11 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold tabular-nums
                    border-2 rounded-xl shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500
                    transition-all duration-200
                    ${error 
                      ? 'border-rose-300 bg-rose-50' 
                      : digit 
                        ? 'border-primary-500 bg-primary-50/80 text-primary-900' 
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }
                  `}
                  disabled={loading}
                />
              ))}
            </div>

            {error && (
              <div className="bg-rose-50/90 border border-rose-200/80 text-rose-900 px-4 py-3 rounded-xl text-sm flex items-center">
                <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={loading}
              disabled={loading}
            >
              Verify OTP
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 space-y-4">
            <div className="text-center">
              <p className={`text-sm ${timer > 0 ? 'text-slate-600' : 'text-rose-600 font-semibold'}`}>
                {timer > 0 ? (
                  <>Code expires in <span className="font-semibold">{formatTime(timer)}</span></>
                ) : (
                  'Code expired'
                )}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={!canResend || loading}
                className={`
                  text-sm font-semibold py-2.5 px-4 rounded-xl transition-all duration-200
                  ${canResend && !loading
                    ? 'text-primary-700 hover:bg-primary-50/90 border border-transparent hover:border-primary-100'
                    : 'text-slate-400 cursor-not-allowed'
                  }
                `}
              >
                {canResend ? 'Resend Code' : `Resend in ${formatTime(timer)}`}
              </button>

              <button
                type="button"
                onClick={onBack}
                disabled={loading}
                className="text-sm text-slate-600 hover:text-slate-900 font-semibold py-2.5 px-4 rounded-xl hover:bg-slate-100/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
